import { ChatStatus, useStores } from '@littlebird/sdk';
import { useParams } from '@tanstack/react-router';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState, type WheelEvent } from 'react';
import { toast } from 'sonner';
import { VList, type VListHandle } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import Message from '@/components/blocks/Message/Message';
import { Button, Text, XStack, YStack } from '@/components/ui';
import TextArea from '@/components/ui/TextArea';
import { cn } from '@/lib/utils';

import { type GroupedMessage } from './arena.types';

export const ArenaMain = observer(() => {
  const { arenaId } = useParams({ strict: false });
  const { arenas: arenaStore } = useStores();

  if (!arenaId) return null;

  const arena = computed(() => arenaStore.get(arenaId)).get();

  if (!arena) {
    return null;
  }
  return <ArenaSession arenaId={arenaId} />;
});

const ArenaSession = observer(({ arenaId }: { arenaId: string }) => {
  return (
    <YStack className="gap-4 size-full">
      <ListView arenaId={arenaId} />
      <ArenaComposer arenaId={arenaId} />
    </YStack>
  );
});

const ArenaComposer = observer(({ arenaId }: { arenaId: string }) => {
  const { arenas: arenaStore } = useStores();
  const isBusy = arenaStore.isBusy(arenaId);

  const [question, setQuestion] = useState('');

  return (
    <YStack className="relative w-full h-24 px-4 pb-4 gap-2">
      <TextArea
        placeholder="Ask a follow up question"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (isBusy) {
              try {
                await arenaStore.stopStreaming(arenaId);
              } catch (err: any) {
                toast.error('Failed to stop streaming', {
                  description: err.message,
                  dismissible: true,
                });
              }
            } else {
              try {
                arenaStore.sendMessage({
                  arenaId,
                  content: question,
                  useTodos: false,
                });
                setQuestion('');
              } catch (err: any) {
                toast.error('Failed to send message', {
                  description: err.message,
                  dismissible: true,
                });
              }
            }
          }
        }}
      />
      <XStack className="absolute bottom-6 right-6 justify-between items-center">
        {isBusy ? (
          <Button
            size="dense"
            variant="destructive"
            onClick={async () => {
              try {
                await arenaStore.stopStreaming(arenaId);
              } catch (err: any) {
                toast.error('Failed to stop streaming', {
                  description: err.message,
                  dismissible: true,
                });
              }
            }}
          >
            <Icons.stop className="size-3" />
            Stop All
          </Button>
        ) : (
          <Button
            size="dense"
            disabled={!question.trim()}
            onClick={() => {
              arenaStore.sendMessage({
                arenaId,
                content: question,
                useTodos: false,
              });
              setQuestion('');
            }}
          >
            Send
            <Icons.arrowRight className="size-3" />
          </Button>
        )}
      </XStack>
    </YStack>
  );
});

const ListView = observer(({ arenaId }: { arenaId: string }) => {
  const { arenas: arenaStore } = useStores();
  const messages = arenaStore.getArenaMessages(arenaId);
  const threadContainerRef = useRef<HTMLDivElement>(null);
  const vlistRef = useRef<VListHandle>(null);

  useEffect(() => {
    if (vlistRef.current && messages.length > 0) {
      vlistRef.current.scrollToIndex(messages.length - 1, { align: 'end' });
    }
  }, [messages]);

  if (!messages.length) return null;

  const syncAssistantScrollAcrossRows = (event: WheelEvent<HTMLDivElement>) => {
    const container = event.target as HTMLDivElement;
    const rows =
      threadContainerRef.current?.querySelectorAll(
        '.assistant-scroll-container'
      ) ?? [];
    for (const row of rows) {
      row.scrollLeft = container.scrollLeft;
    }
  };

  return (
    <div className="size-full" ref={threadContainerRef}>
      <VList
        ref={vlistRef}
        className="w-full h-full"
        style={{ height: '100%' }}
        overscan={5}
      >
        {messages.map((message, index) => {
          return (
            <div key={index} className="p-4">
              <YStack className="gap-8">
                {message.role === 'user' && (
                  <XStack className="justify-end">
                    <XStack className="bg-surface-active text-foreground px-4 py-2 rounded-lg sm:max-w-[80%] min-w-[56px] select-text">
                      {message.message.content}
                    </XStack>
                  </XStack>
                )}
                {message.role === 'assistants' && (
                  <XStack
                    className="assistant-scroll-container gap-4 w-full overflow-x-auto scrollbar-hide"
                    onScroll={syncAssistantScrollAcrossRows}
                  >
                    {message.messages.map((message, index) => (
                      <AssistantMessage message={message} key={index} />
                    ))}
                  </XStack>
                )}
              </YStack>
            </div>
          );
        })}
      </VList>
    </div>
  );
});

type AssistantMessageProps = {
  message: GroupedMessage | null;
};

const AssistantMessage = observer(({ message }: AssistantMessageProps) => {
  const [expanded, setExpanded] = useState(true);
  if (!message || message.role !== 'assistant') return null;

  return (
    <YStack className="w-full min-w-72 sm:min-w-96">
      <YStack className="bg-surface border border-border rounded-lg">
        <XStack className="px-2 py-2 border-b border-border justify-between">
          <Text size="xs" fontFamily="mono" className="line-clamp-1 w-full">
            {message.agentName}
          </Text>
          <XStack className="gap-2 items-center">
            {message && <StatusIndicator status={message.chatStatus} />}
            <XStack
              onClick={() => setExpanded(!expanded)}
              className="text-foreground size-3 !hidden"
            >
              {expanded ? (
                <Icons.shrink className="size-3" />
              ) : (
                <Icons.expand className="size-3" />
              )}
            </XStack>
          </XStack>
        </XStack>
        <YStack
          className={cn(
            'p-4 pb-0',
            !expanded && 'h-32 overflow-y-scroll scrollbar-hide'
          )}
        >
          {message &&
            message.messages.map((message) => (
              <Message
                threadId={message.threadId}
                message={message}
                messageIndex={0}
                key={message.id}
              />
            ))}
          {message && message.chatStatus === ChatStatus.Thinking && (
            <ThinkingIndicator />
          )}
        </YStack>
      </YStack>
    </YStack>
  );
});

type StatusIndicatorProps = {
  status: ChatStatus;
};

const StatusIndicator = ({ status }: StatusIndicatorProps) => {
  if (status === ChatStatus.NotSubmitted) return null;

  return (
    <XStack className="gap-2 items-center">
      <Text
        size="xs"
        fontFamily="mono"
        className={cn('capitalize', {
          'animate-pulse':
            status === ChatStatus.Thinking || status === ChatStatus.Streaming,
        })}
      >
        {status}
      </Text>
      <div
        className={cn('size-2 rounded-full', {
          'bg-foreground': status === ChatStatus.Thinking,
          'bg-accent': status === ChatStatus.Streaming,
          'bg-success-8': status === ChatStatus.Done,
          'bg-warning-8': status === ChatStatus.Stopped,
          'bg-danger-8': status === ChatStatus.Errored,
        })}
      />
    </XStack>
  );
};

const ThinkingIndicator = () => {
  return (
    <XStack className="gap-1 mb-4">
      {Array.from({ length: 3 }).map((_, index) => (
        <XStack
          key={index}
          className="size-1 rounded-full bg-foreground-muted animate-pulse"
        />
      ))}
    </XStack>
  );
};
